import { useState, useRef, useEffect } from 'react';

/**
 * Modal — reusable modal shell.
 *
 * Desktop: free-drag anywhere on screen within viewport bounds.
 * Mobile (≤480px): anchored as a bottom sheet; drag the handle downward
 *   past a threshold to dismiss (swipe-to-close). Snaps back if not
 *   dragged far enough.
 *
 * Props:
 *   isOpen   — controls visibility
 *   onClose  — called when backdrop clicked, × pressed, or swiped away
 *   title    — string shown in the header
 *   onBack   — optional; when provided, a ← Back button appears in the header
 *   children — rendered inside the scrollable modal body
 */
export default function Modal({ isOpen, onClose, title, onBack, children }) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [swipeY, setSwipeY] = useState(0);

  // Use refs so event-handler closures always read the latest values without
  // needing to be recreated on every render.
  const isDraggingRef = useRef(false);
  const swipeYRef = useRef(0);

  const isMobile = () => window.innerWidth <= 480;

  // Reset position / swipe state whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      swipeYRef.current = 0;
      setSwipeY(0);

      if (!isMobile()) {
        const modalWidth = Math.min(450, window.innerWidth - 20);
        setPos({
          x: Math.max(10, (window.innerWidth - modalWidth) / 2),
          y: Math.max(20, (window.innerHeight - 420) / 2),
        });
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Drag / swipe handler ──────────────────────────────────────────────────
  const handleDragStart = (e) => {
    if (!e.touches) e.preventDefault();
    isDraggingRef.current = true;

    const mobile = isMobile();
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const initX  = pos.x;
    const initY  = pos.y;
    const initSY = swipeYRef.current;

    const onMove = (ev) => {
      if (!isDraggingRef.current) return;
      if (ev.touches && ev.cancelable) ev.preventDefault();

      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;

      if (mobile) {
        // Only allow downward drag (never negative / upward).
        const dy = Math.max(0, initSY + (cy - startY));
        swipeYRef.current = dy;
        setSwipeY(dy);
      } else {
        let nx = initX + (cx - startX);
        let ny = initY + (cy - startY);
        const el = document.querySelector('.modal-content');
        const w  = el ? el.offsetWidth  : 450;
        const h  = el ? el.offsetHeight : 420;
        nx = Math.max(0, Math.min(nx, window.innerWidth  - w));
        ny = Math.max(0, Math.min(ny, window.innerHeight - h));
        setPos({ x: nx, y: ny });
      }
    };

    const onEnd = () => {
      isDraggingRef.current = false;

      if (mobile) {
        if (swipeYRef.current > 100) {
          // Swiped far enough — dismiss
          onClose();
        }
        // Always snap back (if onClose re-opens it won't matter)
        swipeYRef.current = 0;
        setSwipeY(0);
      }

      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onEnd);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend',  onEnd);
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const contentStyle = isMobile()
    ? {
        transform: `translateY(${swipeY}px)`,
        transition: isDraggingRef.current ? 'none' : 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }
    : {
        position: 'absolute',
        left: pos.x,
        top:  pos.y,
        width: 450,
        maxWidth:  'calc(100vw - 20px)',
        maxHeight: 'calc(100vh - 40px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      };

  return (
    <div className="modal-overlay fade-in" style={{ zIndex: 1000 }}>
      {/* Clicking the backdrop closes the modal */}
      <div className="modal-backdrop" onClick={onClose} />

      <div className="modal-content" style={contentStyle}>
        {/* Drag handle — grab here to reposition (desktop) or swipe down (mobile) */}
        <div
          className="drag-zone"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="drag-indicator" />
        </div>

        {/* Header */}
        <div className={`modal-header ${!title && !onBack ? 'no-title' : ''}`}>
          {onBack && (
            <button
              onClick={onBack}
              className="modal-back-btn"
            >
              ← Back
            </button>
          )}
          {title ? (
            <h3
              className="modal-title"
              style={{
                marginLeft:  onBack ? 'auto' : '0',
                marginRight: onBack ? 'auto' : '0',
              }}
            >
              {title}
            </h3>
          ) : null}
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body — content provided by the consumer */}
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}
